import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createScope, updateScope, geoLocales, type AsoScope, type ScopeInput } from '@/api/aso';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { useToastStore } from '@/store/toast';

const KIND_OPTIONS = [
  { value: 'search', label: 'Search query (e.g. plinko)' },
  { value: 'category', label: 'Play category (e.g. GAME_CASINO)' },
];
const TOP_OPTIONS = [10, 20, 30, 50].map((n) => ({ value: String(n), label: `TOP-${n}` }));
const LOCALE_OPTIONS = [
  { value: 'geo', label: 'Only this GEO’s store languages' },
  { value: 'all', label: 'Every Play locale (≈85 pages per app)' },
];
const SCHEDULE_OPTIONS = [
  { value: 'manual', label: 'Manual — run by hand' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

export function ScopeModal({ scope, onClose }: { scope: AsoScope | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const [form, setForm] = useState<ScopeInput>({
    name: scope?.name ?? '', geo: scope?.geo ?? 'US', kind: scope?.kind ?? 'search', query: scope?.query ?? '',
    top_n: scope?.top_n ?? 30, locales: scope?.locales ?? 'geo', schedule: scope?.schedule ?? 'manual',
    enabled: scope ? scope.enabled === 1 : true,
    keywords_enabled: scope ? scope.keywords_enabled === 1 : false,
  });
  const set = <K extends keyof ScopeInput>(k: K, v: ScopeInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  const geo = form.geo.trim().toUpperCase();
  const localesQ = useQuery({
    queryKey: ['aso-geo-locales', geo],
    queryFn: () => geoLocales(geo),
    enabled: /^[A-Z]{2}$/.test(geo),
    staleTime: Infinity,
  });

  // A search scope is usually named after its sample; fill the name until the operator types one.
  const [nameTouched, setNameTouched] = useState(!!scope);
  useEffect(() => {
    if (nameTouched) return;
    const q = form.query.trim();
    set('name', q ? `${geo || '??'} · ${q} · TOP-${form.top_n}` : '');
  }, [form.query, form.top_n, geo, nameTouched]);

  const save = useMutation({
    mutationFn: () => (scope ? updateScope(scope.id, form) : createScope(form)),
    onSuccess: () => {
      toast(scope ? 'Scope saved' : 'Scope created');
      qc.invalidateQueries({ queryKey: ['aso-scopes'] });
      onClose();
    },
    onError: (e: any) => toast(e?.message ?? 'Failed to save the scope'),
  });

  return (
    <Modal isOpen onClose={onClose} title={scope ? 'Edit scope' : 'New scope'} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="label">GEO</label>
            <input className="input mt-1 uppercase" maxLength={2} value={form.geo} placeholder="US"
              onChange={(e) => set('geo', e.target.value.toUpperCase())} />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Sample</label>
            <Select value={form.kind} options={KIND_OPTIONS} onChange={(v) => set('kind', v as ScopeInput['kind'])} className="mt-1" />
          </div>
        </div>
        <div>
          <label className="label">{form.kind === 'search' ? 'Search query' : 'Play category id'}</label>
          <input className="input mt-1" value={form.query} placeholder={form.kind === 'search' ? 'plinko' : 'GAME_CASINO'}
            onChange={(e) => set('query', form.kind === 'category' ? e.target.value.toUpperCase() : e.target.value)} />
          {form.kind === 'category' && (
            <p className="text-xs text-warning mt-1">
              Play renders category charts client-side; if the run comes back empty, use a search query instead.
            </p>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="label">Sample size</label>
            <Select value={String(form.top_n)} options={TOP_OPTIONS} onChange={(v) => set('top_n', Number(v))} className="mt-1" />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Locales to store</label>
            <Select value={form.locales} options={LOCALE_OPTIONS} onChange={(v) => set('locales', v as ScopeInput['locales'])} className="mt-1" />
            {form.locales === 'geo' && (
              <p className="text-xs text-content-subtle mt-1">
                {localesQ.data ? `Will read: ${localesQ.data.join(', ')}` : 'Type a GEO to see its store languages'}
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className="label">Name</label>
            <input className="input mt-1" value={form.name} onChange={(e) => { setNameTouched(true); set('name', e.target.value); }} />
          </div>
          <div>
            <label className="label">Schedule</label>
            <Select value={form.schedule} options={SCHEDULE_OPTIONS} onChange={(v) => set('schedule', v as ScopeInput['schedule'])} className="mt-1" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-content">
          <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
          Enabled (scheduled runs only start for enabled scopes)
        </label>
        <label className="flex items-center gap-2 text-sm text-content">
          <input type="checkbox" checked={form.keywords_enabled} onChange={(e) => set('keywords_enabled', e.target.checked)} />
          Collect keyword metrics after each snapshot (needs an ASO API token; costs provider credits per app)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !form.name.trim() || !form.query.trim()}>
            {save.isPending ? 'Saving…' : scope ? 'Save' : 'Create'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
